
<template>
    <div class="pcb-grid">
      <div class="pcb-grid__content">
        <!-- 网格区域 -->
        <div 
          class="pcb-grid__container"
          :class="'pcb-grid__container--edge-' + edgeType"
          :style="{
            width: displayWidth + 'px',
            height: displayHeight + 'px',
            '--grid-columns': x,
            '--grid-rows': y
          }"
        >
          <div 
            class="pcb-grid__item" 
            v-for="item in totalItems" 
            :key="item"
          ></div>
        </div>
      </div>
    </div>
  </template>
  
  <script setup>
  import { computed } from 'vue'
  
  const props = defineProps({
    // 总宽度 (mm)
    width: {
      type: Number,
      default: 0
    },
    // 总高度 (mm)  
    height: {
      type: Number,
      default: 0
    },
    // X方向（水平）的个数
    x: {
      type: Number,
      default: 0
    },
    // Y方向（垂直）的个数
    y: {
      type: Number,
      default: 0
    },
    // 最大显示宽度 (px)
    maxDisplayWidth: {
      type: Number,
      default: 100
    },
    // 最大显示高度 (px)
    maxDisplayHeight: {
      type: Number,
      default: 80
    },
    // 边框类型 (both/leftright/updown/none)
    edgeType: {
      type: String,
      default: 'none'
    }
  })
  // 计算总尺寸和单个方块尺寸
  const totalWidth = computed(() => props.width)
  const totalHeight = computed(() => props.height)
  const totalItems = computed(() => props.x * props.y)
  const itemRealWidth = computed(() => props.width / props.x)
  const itemRealHeight = computed(() => props.height / props.y)
  
  // 计算显示尺寸，按宽度保持比例，同时确保可见性
  const displaySize = computed(() => {
    const actualTotalWidth = totalWidth.value
    const actualTotalHeight = totalHeight.value
    
    // 防止除零错误
    if (!actualTotalWidth || !actualTotalHeight) {
      return { width: 100, height: 100 }
    }
    
    const ratio = actualTotalHeight / actualTotalWidth
    
    // 设置可见性阈值
    const minVisibleWidth = Math.max(props.x * 1, 8) // 每列至少1px，最小8px
    const minVisibleHeight = Math.max(props.y * 1.5, 8) // 每行至少1.5px，最小8px
    
    let displayWidth, displayHeight
    
    // 先按最大宽度计算
    displayWidth = props.maxDisplayWidth
    displayHeight = displayWidth * ratio
    
    // 如果高度超过最大高度限制，以高度为基准重新计算
    if (displayHeight > props.maxDisplayHeight) {
      displayHeight = props.maxDisplayHeight
      displayWidth = displayHeight / ratio
    }
    
    // 如果宽度超过最大宽度限制，以宽度为基准重新计算
    if (displayWidth > props.maxDisplayWidth) {
      displayWidth = props.maxDisplayWidth
      displayHeight = displayWidth * ratio
    }
    
    // 确保最小可见性
    if (displayHeight < minVisibleHeight) {
      displayHeight = minVisibleHeight
      displayWidth = displayHeight / ratio
      
      // 如果调整后宽度超过最大限制，在约束范围内给出最佳值
      if (displayWidth > props.maxDisplayWidth) {
        displayWidth = props.maxDisplayWidth
        displayHeight = Math.min(displayWidth * ratio, props.maxDisplayHeight)
        displayHeight = Math.max(displayHeight, minVisibleHeight)
      }
    }
    
    if (displayWidth < minVisibleWidth) {
      displayWidth = minVisibleWidth
      displayHeight = displayWidth * ratio
      
      // 如果调整后高度超过最大限制，在约束范围内给出最佳值
      if (displayHeight > props.maxDisplayHeight) {
        displayHeight = props.maxDisplayHeight
        displayWidth = Math.min(displayHeight / ratio, props.maxDisplayWidth)
        displayWidth = Math.max(displayWidth, minVisibleWidth)
      }
    }
    
    wh.value={
      width: Math.round(displayWidth),
      height: Math.round(displayHeight)
    }
    return {
      width: Math.round(displayWidth),
      height: Math.round(displayHeight)
    }
  })
  const wh=defineModel()
  const displayWidth = computed(() => displaySize.value.width)
  const displayHeight = computed(() => displaySize.value.height)
  
  // 计算单个方块的显示尺寸，确保可见性
  const itemWidth = computed(() => {
    const baseWidth = displayWidth.value / props.x
    return Math.max(baseWidth, 1) // 至少1px宽度
  })
  
  const itemHeight = computed(() => {
    const baseHeight = displayHeight.value / props.y
    return Math.max(baseHeight, 1) // 至少1px高度
  })
  </script>
  
  <style scoped>
  /* Block: pcb-grid */
  .pcb-grid {
    display: inline-block;
    position: relative;
  }
  
  /* Element: pcb-grid__content */
  .pcb-grid__content {
    display: flex;
    align-items: flex-start;
  }
  
  /* Element: pcb-grid__container */
  .pcb-grid__container {
    display: grid;
    grid-template-columns: repeat(var(--grid-columns), 1fr);
    grid-template-rows: repeat(var(--grid-rows), 1fr);
    border: 1px solid #ddd;
    position: relative;
  }
  
  /* Element: pcb-grid__item */
  .pcb-grid__item {
    background-color: #05b302;
    border-right: 1px dashed #ccc;
    border-bottom: 1px dashed #ccc;
    box-sizing: border-box;
  }

  /* Modifier: pcb-grid__container--edge-* */
  .pcb-grid__container--edge-both {
    border: 3px solid #000;
  }

  .pcb-grid__container--edge-leftright {
    border-left: 3px solid #000;
    border-right: 3px solid #000;
  }

  .pcb-grid__container--edge-updown {
    border-top: 3px solid #000;
    border-bottom: 3px solid #000;
  }

  .pcb-grid__container--edge-none {
    border: 1px solid #ddd; /* 保持原有的灰色边框 */
  }

  /* Block: pcb-dimension (未使用，保留备用) */
  .pcb-dimension {
    display: flex;
    justify-content: center;
    margin-bottom: 10px;
  }
  
  .pcb-dimension__line {
    display: flex;
    align-items: center;
    color: #666;
    font-size: 12px;
  }
  
  .pcb-dimension__line--horizontal {
    flex-direction: row;
  }
  
  .pcb-dimension__line--vertical {
    flex-direction: column;
    writing-mode: vertical-rl;
    text-orientation: mixed;
  }
  
  .pcb-dimension__line-start,
  .pcb-dimension__line-end {
    width: 8px;
    height: 1px;
    background-color: #666;
    position: relative;
  }
  
  .pcb-dimension__line--vertical .pcb-dimension__line-start,
  .pcb-dimension__line--vertical .pcb-dimension__line-end {
    width: 1px;
    height: 8px;
    background-color: #666;
  }
  
  .pcb-dimension__value {
    margin: 0 10px;
    white-space: nowrap;
    font-weight: 500;
  }
  
  .pcb-dimension--right {
    margin-left: 15px;
    height: 100%;
    display: flex;
    align-items: center;
  }
  
  .pcb-dimension--right .pcb-dimension__line {
    height: 100%;
    justify-content: center;
  }
  
  .pcb-dimension--right .pcb-dimension__value {
    text-align: center;
    line-height: 1.2;
    margin: 10px 0;
  }
  </style>
  